import { z } from 'zod';
import { lenientDateString, lenientEnum, lenientCurrency } from '../schema-extensions';
import { Frequency } from '../enums/frequency.enum';

/**
 * Lenient schema for deserializing FrequencyValueNode from storage
 */
const FrequencyValueNodeDeserializeSchema = z.object({
  frequency: lenientEnum(Frequency, Frequency.MONTHLY),
  amount: lenientCurrency(0, 0),
  from: lenientDateString(''),
  to: lenientDateString('')
}).transform(val => ({
  frequency: val.frequency,
  amount: val.amount,
  from: val.from,
  to: val.to
}));

/**
 * Strict schema for creating new FrequencyValueNode
 */
const FrequencyValueNodeCreateSchema = z.object({
  frequency: z.nativeEnum(Frequency),
  amount: z.number().min(0, 'Amount must be non-negative'),
  from: z.string().length(10, 'Date must be in YYYY-MM-DD format'),
  to: z.union([
    z.string().length(10, 'Date must be in YYYY-MM-DD format'),
    z.literal(''),
    z.undefined()
  ]).optional()
}).transform(val => ({
  frequency: val.frequency,
  amount: val.amount,
  from: val.from,
  to: val.to || ''
}));

/**
 * FrequencyValueNode interface
 * Represents a value that occurs at a specific frequency over a date range
 */
export interface FrequencyValueNode {
  frequency: Frequency;
  amount: number;
  from: string;
  to: string;
}

/**
 * FrequencyValueNode namespace with factory and deserialization methods
 */
export namespace FrequencyValueNode {
  /**
   * Validation constants for FrequencyValueNode
   */
  export const Constants = {
    DATE_FORMAT_LENGTH: 10,
    MIN_AMOUNT: 0,
  } as const;

  /**
   * Deserialize FrequencyValueNode from unknown data (e.g., from storage)
   * Uses lenient validation with automatic defaults for missing fields
   * 
   * @param data Unknown data to deserialize
   * @returns Valid FrequencyValueNode with defaults applied
   */
  export function deserialize(data: unknown): FrequencyValueNode {
    return FrequencyValueNodeDeserializeSchema.parse(data);
  }

  /**
   * Safely deserialize FrequencyValueNode with error handling
   * 
   * @param data Unknown data to deserialize
   * @returns Success result with data or failure result with error
   */
  export function deserializeSafe(data: unknown): 
    | { success: true; data: FrequencyValueNode }
    | { success: false; error: z.ZodError } 
  {
    const result = FrequencyValueNodeDeserializeSchema.safeParse(data);
    return result.success
      ? { success: true, data: result.data }
      : { success: false, error: result.error };
  }

  /**
   * Create a new FrequencyValueNode with strict validation
   * 
   * @param data Partial frequency value data
   * @returns Valid FrequencyValueNode
   * @throws ZodError if validation fails
   */
  export function create(data: Partial<FrequencyValueNode>): FrequencyValueNode {
    return FrequencyValueNodeCreateSchema.parse(data);
  }

  /**
   * Create a default FrequencyValueNode with empty values
   */
  export function createDefault(): FrequencyValueNode {
    return {
      frequency: Frequency.MONTHLY,
      amount: 0,
      from: '',
      to: ''
    };
  }

  /**
   * Validate if data conforms to FrequencyValueNode structure
   * 
   * @param data Data to validate
   * @returns True if data is a valid FrequencyValueNode
   */
  export function validate(data: unknown): data is FrequencyValueNode {
    return FrequencyValueNodeDeserializeSchema.safeParse(data).success;
  }
}
